/**
 * Terms of use dialog (#769). mode "gate": first visit / new version, no way out but accepting (no close button,
 * Escape and the backdrop do nothing). mode "view": read-only from the footer link, closable.
 * Text: shared/policy/terms.json (a DRAFT until legal reviews it: `draft` shows the notice at the top).
 * Language (owner decision D4): the terms body (sections, intro, attestation, draft details) stays in English, marked
 * lang="en"; the chrome around it (title, version line, DRAFT badge, "provided in English" note, buttons) comes from
 * the i18n catalog (terms.* keys).
 */
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { dateTimeFormat } from '../i18n/format';
import { useLocale, useT } from '../i18n/useT';
import type { TermsDoc } from './policy';

/** "2026-10-18" in the UI language (Thai: Buddhist era); the raw string when it is not a date. */
function effectiveDateLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return dateTimeFormat({ dateStyle: 'long', timeZone: 'UTC' }).format(d);
}

type Props =
  | { terms: TermsDoc; mode: 'gate'; onAccept: () => void; onClose?: never }
  | { terms: TermsDoc; mode: 'view'; onClose: () => void; onAccept?: never };

export default function TermsDialog(props: Props) {
  const { terms, mode } = props;
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onClose = props.mode === 'view' ? props.onClose : undefined;
  const t = useT();
  const { locale } = useLocale();

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4" data-testid={`terms-${mode}`}>
      <div aria-hidden className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-terms-version={terms.version}
        className="card relative flex max-h-[90vh] w-full flex-col rounded-b-none p-4 sm:max-w-lg sm:rounded-card"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 id={titleId} className="text-base font-semibold">
            {t('terms.title')}
          </h2>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="rounded-md px-2 py-1 text-muted hover:bg-surface-2 hover:text-fg"
            >
              ✕
            </button>
          )}
        </div>
        <p className="mb-2 text-[11px] text-muted" data-testid="terms-version">
          {t('terms.version_line', { version: terms.version, date: effectiveDateLabel(terms.effectiveDate) })}
        </p>
        {terms.draft && (
          <p className="mb-2 rounded-md bg-warn/10 px-2 py-1.5 text-xs font-semibold text-warn" data-testid="terms-draft">
            {t('terms.draft_badge')}
          </p>
        )}
        {locale !== 'en' && (
          <p className="mb-2 text-[11px] text-muted" data-testid="terms-english-only">
            {t('terms.english_only')}
          </p>
        )}
        <div
          className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-md border border-border bg-bg p-3 text-xs leading-relaxed"
          data-testid="terms-body"
          lang="en"
          tabIndex={0}
        >
          {terms.draft && terms.draftNotice && (
            <p className="rounded-md bg-warn/10 px-2 py-1.5 text-warn" data-testid="terms-draft-notice">
              {terms.draftNotice}
            </p>
          )}
          {terms.intro && <p className="text-muted">{terms.intro}</p>}
          {terms.sections.map((s) => (
            <section key={s.heading} className="space-y-1">
              <h3 className="text-sm font-semibold">{s.heading}</h3>
              {s.paragraphs.map((p, i) => (
                <p key={i} className="text-muted">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>
        {mode === 'gate' && props.onAccept && (
          <div className="mt-3 space-y-2">
            <p className="text-[11px] text-muted" lang="en">
              {terms.attestation}
            </p>
            <button
              type="button"
              data-autofocus
              data-testid="terms-accept"
              onClick={props.onAccept}
              className="w-full rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-bg"
            >
              {t('terms.accept')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
